import React, { useEffect, useState, useRef, useCallback } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { ButtonGroup } from 'components/ui/button'
import { FooterEditEntity } from 'components/ui/footer'
import { Input } from 'components/ui/input'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { StatusGuard } from 'components/ui/status-guard'
import { StatusSummary } from 'components/ui/status-summary'

import { sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'

import { Page } from 'generated/mos/pagination'
import { VisitorType } from 'generated/mos/org/getty/audio'
import { GetVisitorTypeRequest } from 'generated/mos/org/getty/audioaccess'
import { UpdateVisitorTypeRequest } from 'generated/mos/org/getty/audiomanagement'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'
import { getValidationWarning } from 'domains/org/getty/utils/validation'

import { pick, areObjectsEqual } from 'helpers/core'
import { convertEntityToFormValues } from 'helpers/schema'
import { Status, statusSelector } from 'helpers/status'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { actionCreators, GettyActionCreators } from '../../../actions'
import { GettyStore } from '../../../store'
import { getVisitorTypeSubnav } from '../subnav'
import { VisitorTypeDelete } from '../visitor-type-delete'
import { visitorTypeSchema } from '../../../schemas/visitor-type'

type FormValues = {
  label: string | undefined;
  tag: string | undefined;
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetVisitorTypeRequest' | 'gettyUpdateVisitorTypeRequest' | 'gettyListValidationFailuresRequest' > &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'visitorTypeUpdate' | 'validationFailuresList'>

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly visitorTypeId: string;
};
type Props = ActionProps & ConnectedProps & DirectProps;

export const VisitorTypeUpdateView = (props: Props) => {
  const {
    gettyGetVisitorTypeRequest,
    gettyUpdateVisitorTypeRequest,
    historyPush,
    projectId,
    visitorTypeId,
    visitorTypeUpdate,
    toastNotification,
    validationFailuresList,
    gettyListValidationFailuresRequest,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
  } = props

  // setup form value variables.
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)

  const hasSubmitted = useRef(false)

  const [ deleteVisitorTypeModalOpen, setDeleteVisitorTypeModalOpen ] = useState(false)

  // where to return to if the user wants to navigate back.
  const backLocation = `/project/${projectId}/visitor-types`

  const visitorTypeData = statusSelector.data(visitorTypeUpdate)
  const validationFailuresListData = statusSelector.data(validationFailuresList)

  const visitorType = visitorTypeData ? visitorTypeData.visitorType : undefined
  const visitorTypeRef: VisitorType.Ref = {
    typename: VisitorType.refName,
    id: visitorTypeId,
  }

  // load initial visitorType data.
  useEffect(() => {
    if (visitorTypeUpdate.status === Status.Idle || (visitorType && VisitorType.mustRef(visitorType.ref).id !== visitorTypeId)) {
      gettyGetVisitorTypeRequest({
        type: GetVisitorTypeRequest.refName,
        visitorTypeRef: visitorTypeRef
      })
      VisitorType.refName
      hasSubmitted.current = false
    }

    releaseControlsListReleaseControlsRequest([visitorTypeRef])

    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [visitorTypeRef]
      }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps


  const getResolvedFormValues = (keys: ReadonlyArray<keyof VisitorType.Entity>): { [key: string]: any} => {
    const visitorTypeValues: Partial<VisitorType.Entity> = pick({
      ...VisitorType.defaults,
      ...visitorType
    }, ...keys)
    return convertEntityToFormValues<VisitorType.Entity>(visitorTypeValues, visitorTypeSchema)
  }

  // setup the initial form values.
  const initialFormValues: FormValues | undefined = visitorType ?
    getResolvedFormValues([ 'label', 'tag']) as FormValues:
    undefined

  useEffect(() => {
    // once the entity is ready, initialise the form values, or reinitialise once an update was
    // successful.
    if (visitorTypeUpdate.status === Status.Ready && initialFormValues) {
      setFormBag(createFormBag(initialFormValues))
    }

    // if the status is ready and the data had been submitted, trigger success toast.
    if (visitorTypeUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([visitorTypeRef])
      toastNotification({ type: 'success', text: 'Visitor Type updated' })
      hasSubmitted.current = false
      gettyListValidationFailuresRequest({
        ...ListValidationFailuresRequest.defaults,
        page: {
          ...Page.defaults,
          pageSize: 100,
        },
        filter: {
          ...ListValidationFailuresRequest.ByRef.defaults,
          entityRefs: [visitorTypeRef]
        }
      })
    }

    // if the status is stale, trigger failure toast.
    if (visitorTypeUpdate.status === Status.Stale) {
      toastNotification({ type: 'error', text: 'Can’t save because of an unknown error.' })
      hasSubmitted.current = false
    }
  }, [ visitorTypeUpdate.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  // handle the form ui.
  const modified = initialFormValues && visitorTypeData && formBag && (
    !areObjectsEqual(initialFormValues, formBag.values)
  )

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}

    return errors
  }

  // attempt to submit validated data to the api.
  const submit = (formBag: FormBag<FormValues>, visitorType: VisitorType.Entity) => {
    const bag = validateFormBag(formBag, validateForm)
    setFormBag(bag)

    if (!bag.valid) {
      return false
    }

    hasSubmitted.current = true
    const values = formBag.values;
    const entityValues = Object.entries(values).reduce((accumulator, currentValue) => {
      const key = currentValue[0] as keyof VisitorType.Entity
      const value = currentValue[1]
      return {
        ...accumulator,
        [key]: visitorTypeSchema[key] ? visitorTypeSchema[key].write(value, visitorType[key]) : value
      }
    }, {})

    gettyUpdateVisitorTypeRequest({
      ...UpdateVisitorTypeRequest.defaults,
      visitorTypeRef: VisitorType.mustRef(visitorType.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          'label',
          'tag',
        ] as any,
      },
      ...entityValues,
    })

    return false
  }

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: visitorTypeRef,
      bundles,
    })
  }

  const failures = validationFailuresListData ? validationFailuresListData.find(({ ref }) => ref && ref.id === visitorTypeId) : undefined
  const failureFields = failures && failures.issues

  const tag = visitorType && visitorType.tag && visitorTypeSchema.tag ? visitorTypeSchema.tag.read(visitorType.tag) as string : ''

  return (
    <>
      {deleteVisitorTypeModalOpen && (
        <VisitorTypeDelete
          historyPush={historyPush}
          projectId={projectId}
          onCancel={() => setDeleteVisitorTypeModalOpen(false)}
          visitorType={visitorType}
        />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit Visitor Type"
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        title={tag}
        toolbar={() => (
          <ButtonGroup>
            <ReleaseControlUpdate
              hasBorder
              entityRef={visitorTypeRef}
              updateHandler={releaseControlUpdate}
            />
            <PopOverMenu hasBorder>
              <PopOverMenuButton onClick={() => setDeleteVisitorTypeModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}
        summary={failureFields ? () => failureFields && <StatusSummary warning={[{ text: `The following fields are required to publish this visitor type: ${failureFields.map(({ attributeDisplayName }) => attributeDisplayName).join(', ') }.` }]} /> : undefined}
        subnav={() => (
          <SecondaryNavigation
            navItems={getVisitorTypeSubnav(projectId, visitorTypeId)}
            modified={modified}
            historyPush={historyPush}
          />
        )}
        content={() => (
          <StatusGuard status={visitorTypeUpdate} mode="form">
            {formBag && visitorType && (
            <form noValidate onSubmit={(event) => { event.preventDefault() }}>
              <FormData bag={formBag} onUpdate={(event) => { setFormBag(event.bag) }} validate={() => ({})} >
                <Input
                  label="Label"
                  field="label"
                  warning={getValidationWarning<VisitorType.Entity>('label', failureFields)}
                />

                <Input
                  label="Tag"
                  field="tag"
                  warning={getValidationWarning<VisitorType.Entity>('tag', failureFields)}
                />
              </FormData>
            </form>
              )}
          </StatusGuard>
        )}
        sidebar={() => (
          <></>
        )}
        footer={() => formBag && visitorType && (
          <FooterEditEntity
            entity={visitorTypeUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBag, visitorType)
              return false
            }}
          />
        )}
      />
    </>
  )
}

export const VisitorTypeUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, "visitorTypeUpdate", "validationFailuresList"),
  }),
  {
    ...pick(actionCreators, 'gettyGetVisitorTypeRequest', 'gettyUpdateVisitorTypeRequest', 'gettyListValidationFailuresRequest'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(VisitorTypeUpdateView)
